import * as React from "react";
import RequestInvite from "models/brand/request-invite";
import { OTS_REQUEST_ACCESS } from "constants/analytics";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import Dialog from "components/dialog";
import AlertDialog from "components/dialog/alert";
import Button from "components/form/button";
import { Form } from "formsy-react";
import Input from "components/form/text-field";
import me from "models/me";
import { Map } from "immutable";
import * as validators from "lib/validators";
import { merge, $, media, presets } from "glamor";
import { DATATEST } from "constants/integration";

interface IRequestAccessProps {
    open: boolean;
    model?: any;
    onRequestClose: any;
    email?: string;
}

interface IRequestAccessState {
    error: any;
    success: boolean;
    submitting: boolean;
    errors: Map<any, any>;
    name?: string;
    email?: string;
    store?: string;
    phone?: string;
}

export class RequestAccess extends React.Component<IRequestAccessProps, IRequestAccessState> {
    _form: any;
    submitting = false;

    styles = merge(
        $(" > p", {
            marginBottom: "16px",
        }),
        $(" .input-row", {
            display: "flex",
            flexWrap: "wrap",
        }),
        $(" .input-row > div", {
            flexShrink: 0,
            flexGrow: 0,
            marginBottom: "16px",
        }),
        $(" .input-row > div.six", {
            width: "100%",
        }),
        $(" .input-row > div.four", {
            width: "100%",
        }),
        media(
            presets.phablet,
            merge(
                $(" .input-row > div", {
                    flexShrink: 0,
                    flexGrow: 0,
                    marginLeft: "4%",
                }),
                $(" .input-row > div:first-child", {
                    marginLeft: "0px",
                }),
                $(" .input-row > div.six", {
                    width: "48%",
                }),
                $(" .input-row > div.four", {
                    width: "30.6666666667%",
                })
            )
        )
    );

    constructor(props) {
        super(props);
        this.state = {
            error: null,
            success: false,
            submitting: false,
            errors: Map(),
            name: null,
            email: null,
            store: null,
            phone: null,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.open && !this.props.open) {
            this.setState({
                error: null,
                success: false,
                errors: Map(),
                name: null,
                email: null,
                store: null,
                phone: null,
            });
        }
    }

    handleSubmit = (form) => {
        const model = new RequestInvite({ brandId: this.props.model._id });
        if (!this.submitting) {
            this.submitting = true;
            this.setState({ submitting: true });
            model.save(form, {
                success: () => {
                    this.submitting = false;
                    this.setState({ success: true, submitting: false });
                    me.trackAction(OTS_REQUEST_ACCESS, {
                        ...form,
                        brand: this.props.model._id,
                    });
                },
                error: (err) => {
                    this.submitting = false;
                    this.setState({
                        submitting: false,
                        error: err.message || format("There was an error submitting your request. Please try again."),
                    });
                },
            });
        }
    };

    handleSubmitButtonTap = () => {
        const { name, store, email, phone } = this.state;
        let errors = this.state.errors;
        if (!name) {
            errors = errors.set("name", format("This field is required."));
        }

        if (!store) {
            errors = errors.set("store", format("This field is required."));
        }

        if (!email) {
            errors = errors.set("email", format("This field is required."));
        } else if (!validators.default.email(email)) {
            errors = errors.set("email", format("Enter a valid email address."));
        }

        if (!phone) {
            errors = errors.set("phone", format("This field is required."));
        }
        const errorKeys = errors.keySeq().toArray();

        if (!errorKeys.length) {
            this._form.submit();
        } else {
            this.setState({ errors });
        }
    };

    handleChangeName = (event: any, name: string) => {
        const { errors } = this.state;
        this.setState({ name });
        if (name) {
            this.setState({ errors: errors.delete("name") });
        }
    };

    handleChangeStore = (event: any, store: string) => {
        const { errors } = this.state;
        this.setState({ store });
        if (store) {
            this.setState({ errors: errors.delete("store") });
        }
    };

    handleChangeEmail = (event: any, email: string) => {
        const { errors } = this.state;
        this.setState({ email });
        if (email && validators.default.email(email)) {
            this.setState({ errors: errors.delete("email") });
        } else if (!validators.default.email(email)) {
            this.setState({ errors: errors.set("email", format("Enter a valid email address.")) });
        }
    };

    handleChangePhone = (event: any, phone: string) => {
        const { errors } = this.state;
        this.setState({ phone });
        if (phone) {
            this.setState({ errors: errors.delete("phone") });
        }
    };

    render() {
        const { onRequestClose, open, model } = this.props;
        const { success, submitting, errors } = this.state;

        if (success) {
            return (
                <AlertDialog title={format("Sweet!")} onRequestClose={onRequestClose} open={open}>
                    <p>{format("Your request has been successfully sent!")}</p>
                </AlertDialog>
            );
        }

        return (
            <Dialog
                title={format("Request Access")}
                actions={[
                    <Button
                        data-test={DATATEST.LOGIN.SEND_REQUEST}
                        key={"send_request"}
                        primary={true}
                        disabled={submitting}
                        label={format("Send Request")}
                        onClick={this.handleSubmitButtonTap}
                    />,
                ]}
                open={open}
                autoScrollBodyContent={true}
                onRequestClose={onRequestClose}
            >
                <div {...this.styles}>
                    <p>
                        <FormatMessage variables={{ companyName: model.name }}>
                            Send a message to |companyName|. Tell them you'd like to view their brand on NuORDER!
                        </FormatMessage>
                    </p>
                    <Form onValidSubmit={this.handleSubmit} ref={(el) => (this._form = el)}>
                        {this.state.error && (
                            <div className="alert alert-error" role="alert">
                                {this.state.error}
                            </div>
                        )}

                        <div className="input-row">
                            <div data-test={DATATEST.LOGIN.YOUR_NAME} className="column six">
                                <Input
                                    name="name"
                                    label={format("Your Name")}
                                    fullWidth={true}
                                    errorText={errors.get("name") ? errors.get("name") : null}
                                    onChange={this.handleChangeName}
                                />
                            </div>

                            <div data-test={DATATEST.LOGIN.STORE_NAME} className="column six">
                                <Input
                                    name="store"
                                    label={format("Store Name")}
                                    fullWidth={true}
                                    errorText={errors.get("store") ? errors.get("store") : null}
                                    onChange={this.handleChangeStore}
                                />
                            </div>
                        </div>

                        <div className="input-row">
                            <div data-test={DATATEST.LOGIN.REQUEST_EMAIL} className="column four">
                                <Input
                                    name="email"
                                    label={format("Email")}
                                    fullWidth={true}
                                    errorText={errors.get("email") ? errors.get("email") : null}
                                    onChange={this.handleChangeEmail}
                                />
                            </div>

                            <div data-test={DATATEST.LOGIN.REQUEST_PHONE} className="column four">
                                <Input
                                    name="phone"
                                    type="tel"
                                    label={format("Phone")}
                                    fullWidth={true}
                                    errorText={errors.get("phone") ? errors.get("phone") : null}
                                    onChange={this.handleChangePhone}
                                />
                            </div>

                            <div className="column four">
                                <Input name="website" type="url" label={format("Website")} fullWidth={true} />
                            </div>
                        </div>

                        <Input
                            name="brands"
                            label={format("Brands Stocked")}
                            placeholder={format("Enter some brands you stock")}
                            fullWidth={true}
                            multiLine={true}
                            rows={2}
                        />

                        <Input
                            name="store_classes"
                            label={format("Store Classifications")}
                            placeholder={format("Women's, Men's, Contemporary, etc.")}
                            fullWidth={true}
                            multiLine={true}
                            rows={2}
                        />

                        <Input
                            name="store_description"
                            label={format("Message")}
                            placeholder={format(
                                "Tell the brand all about your store. The more detailed you are, the more excited they will be to contact you!"
                            )}
                            fullWidth={true}
                            multiLine={true}
                            rows={2}
                        />
                    </Form>
                </div>
            </Dialog>
        );
    }
}

export default RequestAccess;
